import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useAfterFirstPaint } from '@/lib/useAfterFirstPaint';

/**
 * The assistant is global: one window, reachable from every page, that
 * minimises instead of closing so a long answer is never lost when you go
 * look at something. The state survives navigation (the window lives above the
 * router) and reloads (localStorage, per user).
 * See docs/AI_ASSISTANT.md.
 */
export type AiWindowState = 'closed' | 'open' | 'minimized';

interface AiAssistantApi {
  state: AiWindowState;
  open: () => void;
  minimize: () => void;
  close: () => void;
  toggle: () => void;
}

const STORAGE_PREFIX = 'mm:aiAssistant:v1:';
const AiAssistantContext = createContext<AiAssistantApi | null>(null);

function storageKey(userId: number | null): string | null {
  return userId ? `${STORAGE_PREFIX}${userId}` : null;
}

export function AiAssistantProvider({ children }: { children: ReactNode }) {
  const { currentUser } = useAuth();
  // Restored once the page has painted: with the stored user present from the
  // first render (AuthContext), restoring the assistant at once landed in the
  // tasks that draw the screen (a table scored ~3 points lower for it).
  const shellReady = useAfterFirstPaint();
  const userId = shellReady ? (currentUser?.id ?? null) : null;
  const [state, setState] = useState<AiWindowState>('closed');

  // Restore per user; a different user in the same browser starts closed.
  useEffect(() => {
    const key = storageKey(userId);
    if (!key) {
      setState('closed');
      return;
    }
    const stored = localStorage.getItem(key);
    setState(stored === 'open' || stored === 'minimized' ? stored : 'closed');
  }, [userId]);

  useEffect(() => {
    const key = storageKey(userId);
    if (!key) return;
    localStorage.setItem(key, state);
  }, [state, userId]);

  const open = useCallback(() => setState('open'), []);
  const minimize = useCallback(() => setState((prev) => (prev === 'open' ? 'minimized' : prev)), []);
  const close = useCallback(() => setState('closed'), []);
  const toggle = useCallback(() => setState((prev) => (prev === 'open' ? 'minimized' : 'open')), []);

  const value = useMemo(
    () => ({ state, open, minimize, close, toggle }),
    [state, open, minimize, close, toggle],
  );

  return <AiAssistantContext.Provider value={value}>{children}</AiAssistantContext.Provider>;
}

export function useAiAssistant(): AiAssistantApi {
  const ctx = useContext(AiAssistantContext);
  if (!ctx) throw new Error('useAiAssistant must be used inside <AiAssistantProvider>');
  return ctx;
}
